<template>
  <ModuleBase
    v-bind="$attrs"
    :fullHeight="fullHeight"
    :navItems="links"
    :loading="(cashupEnabled && !cashupLoaded) || (loadFiskalyOrganization && fiskalyOrganizationLoading)"
    :gray="!fullHeight && route.name !== 'register-payment'"
    :noPadding="
      fullHeight ||
      route.name === 'register-payment' ||
      route.name === 'salonized-pay'
    "
    :disableIntercomPadding="
      route.name === 'register-builder' || route.name === 'register-payment'
    "
  />
  <FiskalyErrorModal
    v-if="showFiskalyErrorModal"
    @refetch="refetchFiskalyOrganization"
    :loading="fiskalyOrganizationLoading"
  />
  <FiskalyEnableModal v-if="showFiskalyEnableModal" />
</template>

<script
  setup
  lang="ts"
>
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { brandNamePay } from '@/helpers/brand-name';
import { orderKey } from '@/helpers/compliant-register';
import ModuleBase from '@/modules/ModuleBase.vue';
import FiskalyEnableModal from '@/modules/register/FiskalyEnableModal.vue';
import FiskalyErrorModal from '@/modules/register/FiskalyErrorModal.vue';
import { FETCH_FIKALY_ORGANIZATION } from '@/modules/register/graphql';
import { useRegisterStore } from '@/modules/register/stores/register';
import { usePageLayoutStore } from '@/stores/page-layout';
import { useRegisterDrawerStore } from '@/stores/register-drawer';
import { useUserStore } from '@/stores/user';
import unleash from '@/unleash';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const { cashupEnabled } = storeToRefs(useRegisterStore());
const { screenSize } = storeToRefs(usePageLayoutStore());
const { hasFeatureFlag } = storeToRefs(useUserStore());
const { isLoaded: cashupLoaded } = storeToRefs(useRegisterDrawerStore());
const { getDrawerStatus } = useRegisterDrawerStore();

const loadFiskalyOrganization = computed(() =>
  hasFeatureFlag.value('fiskaly-sign-enabled'),
);
const {
  result: fiskalyOrganization,
  refetch: refetchFiskalyOrganization,
  loading: fiskalyOrganizationLoading,
} = useQuery(
  FETCH_FIKALY_ORGANIZATION,
  {},
  { enabled: loadFiskalyOrganization },
);

const showFiskalyErrorModal = computed(
  () =>
    unleash.isEnabled('EnableFiskalySign') &&
    fiskalyOrganization.value?.fetchFiskalyOrganization &&
    !fiskalyOrganization.value?.fetchFiskalyOrganization.available,
);
const showFiskalyEnableModal = computed(
  () =>
    unleash.isEnabled('EnableFiskalySign') &&
    hasFeatureFlag.value('compliant-register') &&
    hasFeatureFlag.value('fiskaly-sign') &&
    !hasFeatureFlag.value('fiskaly-sign-enabled'),
);

const hasSalonizedPay = computed(() => unleash.isEnabled('SalonizedPay'));

// Salons without the register module only see a full-height landing page, except on Salonized
// Pay: its dashboard is taller than the screen, and a full-height page cuts off its background
const fullHeight = computed(
  () =>
    !!route.matched.find(
      (r) =>
        r.name === 'register-builder' ||
        (r.name === 'register-payment' && screenSize.value !== 's'),
    ) ||
    (!hasFeatureFlag.value('module-register') &&
      route.name !== 'salonized-pay'),
);

const showPrepaidCards = computed(
  () =>
    !(
      unleash.isEnabled('EnableFiskalySign') &&
      hasFeatureFlag.value('fiskaly-sign')
    ),
);

const links = computed(() => {
  let items: { text: string; name: string; highlighted?: boolean }[] = [
    { text: t('global.pages.builder'), name: 'register-builder' },
    { text: t(`global.pages.${orderKey('plural')}`), name: 'sales' },
    { text: t('global.pages.day_overview'), name: 'day-overview' },
    { text: t('global.pages.drawer'), name: 'drawer' },
  ];
  if (cashupEnabled.value) {
    items.push({ text: t('global.pages.cashups'), name: 'cashups' });
  }
  if (hasSalonizedPay.value) {
    items.push({
      text: t('global.pages.salonized_pay', brandNamePay()),
      name: 'salonized-pay',
    });
  }
  items = [...items, { text: t('global.pages.giftcards'), name: 'gift-cards' }];
  if (showPrepaidCards.value) {
    items.push({
      text: t('global.pages.prepaid_cards'),
      name: 'prepaid-cards',
    });
  }
  return items;
});

if (cashupEnabled.value && cashupLoaded.value) {
  getDrawerStatus();
}

watch(
  () => route.name,
  (newValue, oldValue) => {
    const isFRFiskalyUser =
      hasFeatureFlag.value('compliant-register') &&
      hasFeatureFlag.value('fiskaly-sign-enabled');
    const newRouteIsPayment = newValue === 'register-payment';

    // Redirect to checkout page when visiting the payment page directly
    if (newRouteIsPayment && !oldValue) {
      router.replace({ name: 'register-builder' });
    } else if (isFRFiskalyUser && newRouteIsPayment) {
      const { mutate: abortCreateIntention } = useMutation(
        gql`
          mutation AbortCreateIntention($input: AbortCreateIntentionInput!) {
            abortCreateIntention(input: $input) {
              success
            }
          }
        `,
        { variables: { input: {} } },
      );
      abortCreateIntention();
    }
  },
  { immediate: true },
);
</script>
